import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import { CollapseBlock, QuestionModal } from '../../../../../common/index.jsx';
import { SeriousDeleteQuestion } from '../../../../../common/DeleteQuestion/SeriousDeleteQuestion.jsx';

import { CurrentJobsConfig } from './Configurations/CurrentJobsConfig.jsx';
import { MyJobsConfig } from './Configurations/MyJobsConfig.jsx';
import { WaitingJobsConfig } from './Configurations/WaitingJobsConfig.jsx';
import { ArchivedJobsConfig } from './Configurations/ArchivedJobsConfig.jsx';

import { FIJ_JOB_STATUS } from '../../../../../../utils/constants/enums.js';

import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { useDataGridDateRangeFilter } from '../../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { useStateFriPraxDataGridFilter } from '../../../../../common/DataGrid/filters/DataGridFilters.jsx';
import TableFilter from '../../../../../common/TableFilter/TableFilter.jsx';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import { FileSearchOutlined } from '@ant-design/icons';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';

const TABLE_FILTER_TITLE = 'Filter FRI praxí';

export const INTERNAL_JOBS_FILTER_LABELS = {
  mine: 'Moje praxe',
  current: 'Aktívne praxe',
  waiting: 'Čakajúce na schválenie',
  archived: 'Archivované praxe',
};

const FILTER_OPTIONS_LABEL = INTERNAL_JOBS_FILTER_LABELS;
const FijJobsTable = ({
  user,
  internalJobs,
  onApproveClick,
  onRejectClick,
  onArchiveClick,
  onDeleteClick,
  onDeleteInterestClick,
  fromPublic,
  selectedView: controlledSelectedView,
  onSelectedViewChange,
  filterOptionsOverride,
  isFijAdmin,
  isFijLecturer,
  isFijIntern,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const myUserId = user?.id;

  const hasForApprovalParam = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.has('forApproval');
  }, [location.search]);

  const derivedFilterOptions = useMemo(() => {
    const options = [
      {
        value: 'current',
        label: FILTER_OPTIONS_LABEL.current,
        count: undefined,
      },
    ];

    if (fromPublic) return options;

    if (isFijLecturer) {
      options.push({ value: 'mine', label: FILTER_OPTIONS_LABEL.mine });
    }

    if (isFijAdmin) {
      options.push({
        value: 'waiting',
        label: FILTER_OPTIONS_LABEL.waiting,
        count: internalJobs.filter(
          (job) => job.status === FIJ_JOB_STATUS.WAITING,
        ).length,
      });
    }

    options.push({ value: 'archived', label: FILTER_OPTIONS_LABEL.archived });
    return options;
  }, [isFijLecturer, isFijAdmin, internalJobs, fromPublic]);

  const filterOptions = filterOptionsOverride ?? derivedFilterOptions;

  const getDefaultSelectedView = () => {
    if (fromPublic) return 'current';
    if (isFijIntern) return 'current';

    if (hasForApprovalParam && isFijAdmin) return 'waiting';

    const view = localStorage.getItem(TABLE_FILTER_TITLE) || '';
    if (filterOptions.some((option) => option.value === view)) return view;
    if (isFijLecturer) return 'mine';
    if (isFijAdmin) return 'waiting';
    return 'current';
  };

  const [internalSelectedView, setInternalSelectedView] = useState(
    getDefaultSelectedView,
  );

  const selectedView = controlledSelectedView ?? internalSelectedView;
  const setSelectedView = onSelectedViewChange ?? setInternalSelectedView;

  useEffect(() => {
    if (controlledSelectedView === undefined) {
      if (isFijIntern) {
        setInternalSelectedView('current');
        return;
      }
      localStorage.setItem(TABLE_FILTER_TITLE, selectedView);
    }
  }, [controlledSelectedView, selectedView, isFijIntern]);

  useEffect(() => {
    if (controlledSelectedView !== undefined) {
      return;
    }

    if (fromPublic || isFijIntern) {
      return;
    }

    if (hasForApprovalParam && isFijAdmin && selectedView !== 'waiting') {
      setInternalSelectedView('waiting');
    }
  }, [
    controlledSelectedView,
    fromPublic,
    isFijIntern,
    hasForApprovalParam,
    isFijAdmin,
    selectedView,
  ]);

  useEffect(() => {
    if (controlledSelectedView !== undefined) {
      return;
    }
    if (isFijIntern) {
      setInternalSelectedView('current');
      return;
    }
    if (
      !filterOptions.some((option) => option.value === internalSelectedView)
    ) {
      const fallback = filterOptions[0]?.value ?? 'current';
      setInternalSelectedView(fallback);
    }
  }, [
    filterOptions,
    controlledSelectedView,
    internalSelectedView,
    isFijIntern,
  ]);

  const [rowToBeApproved, setRowToBeApproved] = useState(null);
  const [rowToBeRejected, setRowToBeRejected] = useState(null);
  const [rowToBeArchived, setRowToBeArchived] = useState(null);
  const [rowToBeDeletedInterest, setRowToBeDeletedInterest] = useState(null);

  const filterHooks = {
    nameTextFilterProps: useDataGridTextFilter('name'),
    supervisorTextFilterProps: useDataGridTextFilter('supervisor_full_name'),
    fromDateRangeFilterProps: useDataGridDateRangeFilter('date_from'),
    toDateRangeFilterProps: useDataGridDateRangeFilter('date_to'),
    statusTextFilterProps: useStateFriPraxDataGridFilter('status'),
  };

  const getPublicCustomActions = (record) => (
    <div style={{ display: 'flex' }}>
      <IconButton
        icon={<FileSearchOutlined />}
        tooltip={t('Detail')}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`${routes.fijJobDetail}/${record.id}`);
        }}
      />
    </div>
  );

  const filteredData = useMemo(() => {
    if (!internalJobs) return [];

    switch (selectedView) {
      case 'mine':
        return internalJobs.filter(
          (job) =>
            job.supervisor_id === myUserId &&
            job.status !== FIJ_JOB_STATUS.REJECTED &&
            job.archived_at === null,
        );
      case 'waiting':
        return internalJobs.filter(
          (job) =>
            job.status === FIJ_JOB_STATUS.WAITING && job.archived_at === null,
        );
      case 'archived':
        return internalJobs.filter((job) => job.archived_at !== null);
      case 'current':
      default:
        return internalJobs.filter(
          (job) =>
            job.status === FIJ_JOB_STATUS.APPROVED && job.archived_at === null,
        );
    }
  }, [internalJobs, selectedView, myUserId]);

  const configuration = useMemo(() => {
    const configProps = {
      t,
      navigate,
      permissions: {
        isFijAdmin: isFijAdmin,
        isFijIntern: isFijIntern,
        isFijLecturer: isFijLecturer,
        userId: myUserId,
      },
      handlers: {
        openApproveModal: (record) => setRowToBeApproved(record.id),
        openRejectModal: (record) => setRowToBeRejected(record.id),
        openArchiveModal: (record) => setRowToBeArchived(record.id),
        openDeleteInterestModal: (record) =>
          setRowToBeDeletedInterest(record.my_interest_id),
      },
      filterHooks,
      fromPublic,
    };

    switch (selectedView) {
      case 'mine':
        return MyJobsConfig(configProps);
      case 'waiting':
        return WaitingJobsConfig(configProps);
      case 'archived':
        return ArchivedJobsConfig(configProps);
      case 'current':
      default:
        return CurrentJobsConfig(configProps);
    }
  }, [
    selectedView,
    t,
    navigate,
    isFijAdmin,
    isFijLecturer,
    isFijIntern,
    myUserId,
    filterHooks,
    fromPublic,
  ]);

  return (
    <>
      {controlledSelectedView === undefined &&
        !isFijIntern &&
        filterOptions.length > 1 && (
          <TableFilter
            title={t(TABLE_FILTER_TITLE)}
            value={selectedView}
            setValue={setSelectedView}
            options={filterOptions}
          ></TableFilter>
        )}

      <CollapseBlock
        title={
          filterOptions.length > 1
            ? t(FILTER_OPTIONS_LABEL[selectedView])
            : null
        }
      >
        <DataGrid
          rowKey={(record) => record.id}
          columns={configuration.columns}
          dataSource={filteredData
            .slice()
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))}
          permissionsBasic={'fri-internal-jobs-jobs'}
          uniqueTableId={`fij-internal-jobs-${selectedView}-table`}
          onDelete={
            selectedView === 'mine' || isFijAdmin
              ? (record) => onDeleteClick(record.id)
              : undefined
          }
          seriousDeleteQuestion={
            <SeriousDeleteQuestion
              firstText={t('Naozaj chcete vymazať túto tému FRI praxe?')}
              mainNotice={t(
                'Spolu s praxou budú vymazané aj všetky prihlášky a zapísaní študenti na túto FRI prax. Túto akciu nie je možné vrátiť späť!',
              )}
              lastText={t(
                'Prosím uistite sa, že mažete správnu FRI prax, aby ste predišli nežiaducim komplikáciám!',
              )}
            />
          }
          customSettings={(record) =>
            fromPublic
              ? getPublicCustomActions(record)
              : configuration.customActions(record)
          }
          optionsWidth={fromPublic ? 80 : 250}
          onRowDetailClick={
            // Waiting jobs offer the detail only to the admin
            selectedView !== 'waiting' || isFijAdmin
              ? (record) =>
                  navigate(
                    `${fromPublic ? routes.fijJobDetail : routes.cmsFijJobDetail}/${record.id}`,
                  )
              : undefined
          }
        />
      </CollapseBlock>

      {/* Approve modal */}
      <QuestionModal
        chosenPositive={() => {
          onApproveClick(rowToBeApproved);
          setRowToBeApproved(null);
        }}
        question={t(
          'Naozaj chcete schváliť túto tému FRI praxe? Téma FRI praxe sa presunie medzi schválené a bude viditeľná pre študentov.',
        )}
        visible={rowToBeApproved !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeApproved(null)}
        headerTitle={t('Schváliť tému FRI praxe')}
      />

      {/* Reject modal */}
      <QuestionModal
        chosenPositive={() => {
          onRejectClick(rowToBeRejected);
          setRowToBeRejected(null);
        }}
        question={t(
          'Naozaj chcete neschváliť túto tému FRI praxe? Téma bude uložená do archívu a nebude viditeľná pre študentov.',
        )}
        visible={rowToBeRejected !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeRejected(null)}
        headerTitle={t('Neschváliť tému FRI praxe')}
      />

      {/* Archive modal */}
      <QuestionModal
        chosenPositive={() => {
          onArchiveClick(rowToBeArchived);
          setRowToBeArchived(null);
        }}
        question={t(
          'Naozaj chcete archivovať danú tému FRI praxe? Téma FRI praxe bude presunutá do archívu a nebude viac ponúkaná študentom.',
        )}
        visible={rowToBeArchived !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeArchived(null)}
        headerTitle={t('Archivovať tému FRI praxe')}
      />

      {/* Delete interest modal */}
      <QuestionModal
        chosenPositive={() => {
          onDeleteInterestClick(rowToBeDeletedInterest);
          setRowToBeDeletedInterest(null);
        }}
        question={t('Naozaj sa chcete odhlásiť z tejto FRI praxe?')}
        visible={rowToBeDeletedInterest !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeletedInterest(null)}
        headerTitle={t('Odhlásiť sa z FRI praxe')}
      />
    </>
  );
};

FijJobsTable.propTypes = {
  user: PropTypes.object, //will be undefined if called from public
  internalJobs: PropTypes.array.isRequired,
  onApproveClick: PropTypes.func.isRequired,
  onRejectClick: PropTypes.func.isRequired,
  onArchiveClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onDeleteInterestClick: PropTypes.func.isRequired,
  fromPublic: PropTypes.bool.isRequired,
  selectedView: PropTypes.string,
  onSelectedViewChange: PropTypes.func,
  filterOptionsOverride: PropTypes.array,
  isFijAdmin: PropTypes.bool,
  isFijLecturer: PropTypes.bool,
  isFijIntern: PropTypes.bool,
};

export default FijJobsTable;
